import { Text, type Container, type FederatedPointerEvent } from "pixi.js";
import { SpriteText, type PixiPresentationTicker, type PresentationTaskRunner } from "@slot/presentation";
import { formatAudioLogEntry } from "../audio/debug/RecordingAudioService";
import type { AudioService } from "../audio/ports/AudioService";
import type { AudioSettingsStore } from "../audio/AudioSettingsStore";
import type { AutoSpinStore } from "../autospin/AutoSpinStore";
import type { BalanceStore } from "../balance/BalanceStore";
import type { BetLinesPort } from "../bet/BetLinesPort";
import type { BetSystem } from "../bet/BetSystemTypes";
import type { DevRecorders } from "../boot/createCoreServices";
import type { ReplayStatus } from "../boot/flows/replayRound";
import type { RestoreStatus } from "../boot/flows/restorePendingRound";
import type { CanvasOverlayStore } from "../canvas-overlay/CanvasOverlayStore";
import type { OfflineRoundProvider } from "../composition/GameDefinition";
import type { GameRuntime } from "../composition/GameRuntime";
import type { FeatureModeStore } from "../feature-mode/FeatureModeStore";
import type { FeatureStateStore } from "../feature-state/FeatureStateStore";
import type { HapticsSettingsStore } from "../haptics/HapticsSettingsStore";
import type { InteractionBlockStore } from "../interaction-block/InteractionBlockStore";
import type { SlotBootDebugHook, SlotDebugHook } from "./installDebugHook";

/** The stores and services the hook reads. */
export interface RuntimeDebugHookDeps {
  presentationTaskRunner: PresentationTaskRunner;
  interactionBlockStore: InteractionBlockStore;
  canvasOverlayStore: CanvasOverlayStore;
  featureStateStore: FeatureStateStore;
  featureModeStore: FeatureModeStore;
  autoSpinStore: AutoSpinStore;
  balanceStore: BalanceStore;
  betSystem: BetSystem;
  audioService: AudioService;
  audioSettingsStore: AudioSettingsStore;
  hapticsSettingsStore: HapticsSettingsStore;
}

/**
 * Completes `window.__slot` once the runtime exists (decision 38): the handles, the round driving, the readers over the
 * stores and services, the node helpers over the Pixi stage, the logs, the fixture and fault members over the offline
 * server's dev ports (the dev menu drives the same ports). Every title gets the same surface; a title's own members go
 * through `GameDevDefinition.installHook`.
 */
export function installRuntimeDebugHook(input: {
  hook: SlotBootDebugHook;
  runtime: GameRuntime;
  deps: RuntimeDebugHookDeps;
  presentationTicker: PixiPresentationTicker;
  /** The offline server; null on a backend, where the fixture and fault members throw (`clearFixture` does nothing). */
  offlineRounds: OfflineRoundProvider | null;
  stage: Container;
  devRecorders: DevRecorders | null;
  /** A round waiting for the player's Spin (decision 55). */
  playerSpins: { readonly waiting: boolean };
  /** The bet's lines (decision 56). */
  betLines: BetLinesPort;
  getReplayStatus: () => ReplayStatus | null;
  getRestoreStatus: () => RestoreStatus | null;
}): SlotDebugHook {
  const coordinator = input.runtime.getSpinCoordinator();
  const runner = input.deps.presentationTaskRunner;
  const offline = (member: string): OfflineRoundProvider => {
    if (!input.offlineRounds) throw new Error(`${member}: the game is not running on fixtures`);
    return input.offlineRounds;
  };
  const recorders = (): DevRecorders => {
    if (!input.devRecorders) throw new Error("audio log: the core services were built without dev mode");
    return input.devRecorders;
  };
  const hook: SlotDebugHook = Object.assign(input.hook, {
    runtime: input.runtime,
    spinCoordinator: coordinator,
    runner,
    presentationTicker: input.presentationTicker,
    loadFixture(id: string): void {
      offline("loadFixture").setPlaylist([id]);
    },
    loadFixtures(ids: string[]): void {
      offline("loadFixtures").setPlaylist(ids);
    },
    getPlaylist(): string[] {
      return [...(input.offlineRounds?.getPlaylist() ?? [])];
    },
    clearFixture(): void {
      input.offlineRounds?.setPlaylist([]);
    },
    listFixtures(): string[] {
      return (input.offlineRounds?.listFixtures() ?? []).map((fixture) => fixture.id);
    },
    spin(): Promise<void> {
      return coordinator.requestSpin();
    },
    slam(): void {
      coordinator.slamStop();
    },
    setSpeedMode(mode: Parameters<SlotDebugHook["setSpeedMode"]>[0]): void {
      coordinator.setSpeedMode(mode);
    },
    waitForIdle(timeoutMs = 60_000): Promise<void> {
      return new Promise<void>((resolve, reject) => {
        const started = performance.now();
        const check = (): boolean => coordinator.getSnapshot().phase === "idle" && runner.getActiveTaskCount() === 0;
        if (check()) {
          resolve();
          return;
        }
        const tick = (): void => {
          if (check()) {
            input.hook.app.ticker.remove(tick);
            resolve();
          } else if (performance.now() - started > timeoutMs) {
            input.hook.app.ticker.remove(tick);
            reject(new Error(`waitForIdle timed out (phase=${coordinator.getSnapshot().phase} tasks=${runner.getActiveTaskCount()})`));
          }
        };
        input.hook.app.ticker.add(tick);
      });
    },
    getActiveTaskCount(): number {
      return runner.getActiveTaskCount();
    },
    getActiveScopes(): Record<string, number> {
      return runner.getActiveScopes();
    },
    getPhase(): string {
      return coordinator.getSnapshot().phase;
    },
    getInteractionBlocks(): string[] {
      return input.deps.interactionBlockStore.getSnapshot().reasons;
    },
    getCanvasOverlayCount(): number {
      return input.deps.canvasOverlayStore.getSnapshot().open;
    },
    isFeatureActive(): boolean {
      return input.deps.featureStateStore.isFeatureActive();
    },
    getFeatureMode(): string | null {
      return input.deps.featureModeStore.getSnapshot().activeMode?.id ?? null;
    },
    isAudioUnlocked(): boolean {
      const snapshot = input.deps.audioService.control.__getDebugSnapshot?.() as { unlocked?: boolean } | undefined;
      return snapshot?.unlocked === true;
    },
    pressToContinue(): void {
      window.dispatchEvent(new KeyboardEvent("keydown", { code: "Enter", key: "Enter" }));
    },
    isAwaitingSpin(): boolean {
      return input.playerSpins.waiting;
    },
    getBetLines() {
      const snapshot = input.betLines.getSnapshot();
      return { betMinor: input.deps.betSystem.getCurrentBetMinor(), lineBetMinor: snapshot.lineBetMinor, lines: snapshot.lines, options: [...snapshot.options], canChange: snapshot.canChange };
    },
    canGamble(): boolean {
      return coordinator.getSnapshot().canGamble;
    },
    enterGamble(): Promise<void> {
      return coordinator.enterGamble();
    },
    tapNode(label: string): boolean {
      const node = findShownByLabel(input.stage, label);
      if (!node) return false;
      node.emit("pointertap", undefined as unknown as FederatedPointerEvent);
      return true;
    },
    isShown(label: string): boolean {
      return findShownByLabel(input.stage, label) !== null;
    },
    getText(label: string): string | null {
      const node = findShownByLabel(input.stage, label);
      if (node instanceof SpriteText) return node.getText();
      return node instanceof Text ? node.text : null;
    },
    getNodeBounds(label: string): { x: number; y: number; width: number; height: number } | null {
      const node = findShownByLabel(input.stage, label);
      if (!node) return null;
      const b = node.getBounds();
      return { x: b.x, y: b.y, width: b.width, height: b.height };
    },
    getNodeHash(label: string): string | null {
      const node = findShownByLabel(input.stage, label);
      if (!node) return null;
      const { pixels } = input.hook.app.renderer.extract.pixels({ target: node, resolution: 1 });
      let hash = 0x811c9dc5;
      for (let i = 0; i < pixels.length; i += 1) {
        hash ^= pixels[i]!;
        hash = Math.imul(hash, 0x01000193) >>> 0;
      }
      return `${hash.toString(16).padStart(8, "0")}:${pixels.length}`;
    },
    isInteractable(label: string): boolean | null {
      const node = findShownByLabel(input.stage, label) as (Container & { interactable?: boolean }) | null;
      return node && typeof node.interactable === "boolean" ? node.interactable : null;
    },
    getAutoSpin() {
      return input.deps.autoSpinStore.getSnapshot();
    },
    getSpeedMode(): string {
      return coordinator.getSnapshot().speedMode;
    },
    getAudioSettings(): { masterVolume: number; musicEnabled: boolean } {
      const snap = input.deps.audioSettingsStore.getSnapshot();
      return { masterVolume: snap.masterVolume, musicEnabled: snap.musicEnabled };
    },
    isHapticsEnabled(): boolean {
      return input.deps.hapticsSettingsStore.getSnapshot().hapticsEnabled;
    },
    getAudioLog(): string[] {
      return recorders().audio.entries.map(formatAudioLogEntry);
    },
    getHapticsLog(): string[] {
      return [...recorders().haptics.entries];
    },
    clearAudioLogs(): void {
      recorders().audio.clear();
      recorders().haptics.clear();
    },
    getAudioDebugSnapshot(): Record<string, unknown> {
      return (input.deps.audioService.control.__getDebugSnapshot?.() ?? {}) as Record<string, unknown>;
    },
    getReplayStatus(): ReplayStatus | null {
      return input.getReplayStatus();
    },
    getRestoreStatus(): RestoreStatus | null {
      return input.getRestoreStatus();
    },
    getLastRoundId(): string | null {
      return coordinator.getSnapshot().lastRoundId ?? null;
    },
    getBalanceMinor(): string {
      return input.deps.balanceStore.getSnapshot().balanceMinor;
    },
    rejectNextSpin(code?: Parameters<SlotDebugHook["rejectNextSpin"]>[0]): void {
      offline("rejectNextSpin").rejectNextSpin(code);
    },
    failNextSpin(): void {
      offline("failNextSpin").failNextSpin();
    },
    failNextSettlement(count?: number): void {
      offline("failNextSettlement").failNextSettlement(count);
    },
    setSimulatedLatency(ms: number): void {
      offline("setSimulatedLatency").setSimulatedLatency(ms);
    },
  });
  window.__slot = hook;
  return hook;
}

/** The first node labelled `label` inside a VISIBLE subtree (a hidden shell's button is never the answer). */
function findShownByLabel(root: Container, label: string): Container | null {
  if (!root.visible) return null;
  if (root.label === label) return root;
  for (const child of root.children) {
    const hit = findShownByLabel(child, label);
    if (hit) return hit;
  }
  return null;
}
